import { memo } from 'react';

import { Link } from '@tanstack/react-router';

import type { RemoveItemHandler } from '@/_components';
import { FilledStar, Icon, IconLogoAvatar } from '@/_components';
import { DeleteButton } from '@/_containers';
import type { CompanyQueryLinkerWithPivotSchema } from '@/_schemas/companies/company-query-linker-with-pivot-schema';

export type CompanyItemProps = {
  company: CompanyQueryLinkerWithPivotSchema;
  isEditable?: boolean;
  removeItemHandler: RemoveItemHandler<CompanyQueryLinkerWithPivotSchema>;
  canSetPrimary?: boolean;
};

function CompanyItemBase({ company, canSetPrimary = true, isEditable = false, removeItemHandler }: CompanyItemProps) {
  const { id, name, logo, pivot } = company;
  const fullName = name;
  const isPrimary = pivot?.type === 'primary';

  const handleDeleteContact = async () => {
    removeItemHandler(company);
  };

  return (
    <div className="border-c-border-muted text-accent flex h-11 flex-nowrap items-center justify-between border-b dark:text-gray-400">
      <div className="flex items-center gap-1.5">
        <Link
          to="/companies/$id"
          params={{ id: `${id}` }}
          state={{ naturalBack: true }}
          className="group flex items-center gap-2"
        >
          <IconLogoAvatar src={logo?.at(0)?.url} name={fullName} avatarShape="rectangle" />
          <span className="decoration-primary line-clamp-2 text-sm decoration-2 group-hover:underline">{fullName}</span>
        </Link>
      </div>

      <div className="flex items-center gap-1.5">
        {isPrimary && canSetPrimary ? <Icon icon={FilledStar} /> : null}
        {isEditable && (
          <DeleteButton
            type="icon"
            size="sm"
            title="Remove Company?"
            text="The Company record will not be deleted; only its association will be removed."
            yesButton="Yes, Remove"
            aria-label="Remove company"
            callback={handleDeleteContact}
          />
        )}
      </div>
    </div>
  );
}

export const CompanyItem = memo(CompanyItemBase);
